import React from 'react';
import { captureError } from '@/lib/ops/utils';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/** Stale tab after a deploy: old JS references chunk URLs that no longer exist. */
function isLikelyStaleChunkError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || '';
  const name = error.name || '';
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    name === 'ChunkLoadError'
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error | null; reset: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Capture error
    captureError(error, 'error', {
      route: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      meta: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const Fallback = this.props.fallback;
        return <Fallback error={this.state.error} reset={this.reset} />;
      }
      
      const staleChunk = isLikelyStaleChunkError(this.state.error);

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-md">
            <h1 className="text-2xl font-bold text-arc-text-main">Something went wrong</h1>
            <p className="text-arc-text-muted">{this.state.error?.message}</p>
            {staleChunk ? (
              <p className="text-sm text-arc-text-muted">
                This often happens after we ship an update while the tab was still open. Reloading loads the latest
                version.
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => {
                if (staleChunk) {
                  window.location.reload();
                  return;
                }
                this.reset();
              }}
              className="px-4 py-2 bg-arc-primary text-white rounded hover:bg-arc-primary/90 transition-colors"
            >
              {staleChunk ? 'Reload page' : 'Try again'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

